import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  TablePagination,
  Alert,
} from '@mui/material';
import { ResponsiveTable, Column } from '../../components/mobile/ResponsiveTable';
import AddIcon from '@mui/icons-material/Add';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import axiosInstance from '../../lib/axios';
import { TableToolbar, FilterOption, ColumnConfig } from '../../components/TableToolbar';
import type { Supplier, PaymentTerms, PaginatedResponse } from '../../types';
import { palette } from '../../theme/colors';

const fetchSuppliers = async (
  page: number,
  pageSize: number,
  search?: string,
  isActive?: boolean
): Promise<PaginatedResponse<Supplier>> => {
  const params: Record<string, any> = {
    page: page + 1,
    limit: pageSize,
  };

  if (search) {
    params.search = search;
  }

  if (isActive !== undefined) {
    params.isActive = isActive;
  }

  const response = await axiosInstance.get('/suppliers', { params });

  return {
    data: Array.isArray(response.data) ? response.data : response.data.items || [],
    total: response.data.total || (Array.isArray(response.data) ? response.data.length : 0),
  };
};

const paymentTermsLabels: Record<PaymentTerms, string> = {
  net_7: 'Net 7 jours',
  net_15: 'Net 15 jours',
  net_30: 'Net 30 jours',
  net_60: 'Net 60 jours',
  net_90: 'Net 90 jours',
  due_on_receipt: 'Paiement immédiat',
  custom: 'Personnalisé',
};

const filterOptions: FilterOption[] = [
  {
    field: 'isActive',
    label: 'Statut',
    type: 'select',
    options: [
      { value: 'true', label: 'Actif' },
      { value: 'false', label: 'Inactif' },
    ],
  },
];

const getExportColumns = (): ColumnConfig[] => [
  { field: 'name', headerName: 'Nom' },
  { field: 'code', headerName: 'Code' },
  { field: 'contactName', headerName: 'Contact' },
  { field: 'phone', headerName: 'Téléphone' },
  { field: 'email', headerName: 'Email' },
  {
    field: 'defaultPaymentTerms',
    headerName: 'Conditions de paiement',
    valueFormatter: (value) => paymentTermsLabels[value as PaymentTerms] || value,
  },
  {
    field: 'isActive',
    headerName: 'Statut',
    valueFormatter: (value) => (value ? 'Actif' : 'Inactif'),
  },
];

interface SuppliersListProps {
  onCreateClick?: () => void;
  onSupplierClick?: (supplier: Supplier) => void;
  hasB2BAccess?: boolean;
}

export const SuppliersList = ({ onCreateClick, onSupplierClick, hasB2BAccess = true }: SuppliersListProps) => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>({});

  const isActiveFilter = activeFilters.isActive !== undefined && activeFilters.isActive !== ''
    ? activeFilters.isActive === 'true'
    : undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['suppliers', page, pageSize, searchValue, isActiveFilter],
    queryFn: () => fetchSuppliers(page, pageSize, searchValue, isActiveFilter),
  });

  const filteredData = useMemo(() => {
    return data?.data || [];
  }, [data?.data]);

  const handleFilterChange = (field: string, value: string) => {
    setActiveFilters((prev) => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filtering
  };

  const handleClearFilters = () => {
    setActiveFilters({});
    setPage(0);
  };

  const handleRowClick = (supplier: Supplier) => {
    if (onSupplierClick) {
      onSupplierClick(supplier);
    } else {
      navigate(`/app/suppliers/${supplier.id}`);
    }
  };

  // Column definitions for ResponsiveTable
  const tableColumns: Column<Supplier>[] = [
    {
      field: 'name',
      headerName: 'Fournisseur',
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: palette.primaryMuted,
              color: palette.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BusinessIcon sx={{ fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.code}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'contactName',
      headerName: 'Contact',
      render: (value, row) => {
        if (!value && !row.phone && !row.email) {
          return (
            <Typography variant="caption" color="text.secondary">
              Non renseigné
            </Typography>
          );
        }

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {value && (
              <Typography variant="body2">{value}</Typography>
            )}
            {row.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {row.phone}
                </Typography>
              </Box>
            )}
            {row.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  {row.email}
                </Typography>
              </Box>
            )}
          </Box>
        );
      },
    },
    {
      field: 'defaultPaymentTerms',
      headerName: 'Conditions de paiement',
      render: (value) => (
        <Chip
          label={paymentTermsLabels[value as PaymentTerms] || value}
          size="small"
          variant="outlined"
          sx={{ borderRadius: 1 }}
        />
      ),
    },
    {
      field: 'currency',
      headerName: 'Devise',
      render: (value) => (
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
      ),
    },
    {
      field: 'isActive',
      headerName: 'Statut',
      render: (value) => (
        <Chip
          label={value ? 'Actif' : 'Inactif'}
          size="small"
          color={value ? 'success' : 'default'}
          variant={value ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: 'createdAt',
      headerName: 'Créé le',
      render: (value) => {
        const date = new Date(value);
        return (
          <Typography variant="body2">
            {date.toLocaleDateString('fr-FR')}
          </Typography>
        );
      },
    },
  ];

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4">Fournisseurs</Typography>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Gérez vos fournisseurs et leurs informations de contact
          </Typography>
        </Box>
        {onCreateClick && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onCreateClick}
            sx={{ borderRadius: 2 }}
          >
            Nouveau fournisseur
          </Button>
        )}
      </Box>

      {!hasB2BAccess && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Le module B2B fournisseurs n'est pas activé pour votre organisation.
          Contactez votre administrateur pour activer cette fonctionnalité.
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableToolbar
            title="Fournisseurs"
            searchValue={searchValue}
            onSearchChange={setSearchValue}
            searchPlaceholder="Rechercher par nom ou code..."
            filters={filterOptions}
            activeFilters={activeFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            data={filteredData}
            columns={getExportColumns()}
            exportFileName="fournisseurs"
          />

          <ResponsiveTable
            columns={tableColumns}
            rows={filteredData}
            loading={isLoading}
            emptyMessage="Aucun fournisseur trouvé"
            primaryField="name"
            getPrimaryText={(row) => row.name}
            getSecondaryText={(row) => row.code}
            onRowClick={handleRowClick}
          />
          <TablePagination
            component="div"
            count={data?.total || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Lignes par page:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default SuppliersList;
